import railsPostFetch from '@aytm/lib/common/rails-post-fetch'

export type WorkingHoursRange = {
  start_time: number // minutes since Sunday midnight UTC
  end_time: number // minutes since Sunday midnight UTC
}

const WORKING_HOURS_CACHE_KEY = 'skipper_support_working_hours'
const WORKING_HOURS_CACHE_DURATION = 86_400_000 // 24 hours — cache for entire session

// Default working hours: Monday-Friday 9am-9pm EST (UTC-05:00)
// const DEFAULT_WORKING_HOURS: WorkingHoursRange[] = [
//   { start_time: 2280, end_time: 3000 },
//   { start_time: 3720, end_time: 4440 },
//   { start_time: 5160, end_time: 5880 },
//   { start_time: 6600, end_time: 7320 },
//   { start_time: 8040, end_time: 8760 }
// ]

type CachedWorkingHours = {
  ranges: WorkingHoursRange[]
  timestamp: number
}

let pendingFetch: Promise<WorkingHoursRange[] | null> | null = null

export async function fetchWorkingHours(): Promise<WorkingHoursRange[] | null> {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const cached = window.sessionStorage.getItem(WORKING_HOURS_CACHE_KEY)
    if (cached) {
      const parsedCache = JSON.parse(cached) as CachedWorkingHours
      const now = Date.now()
      if (now - parsedCache.timestamp < WORKING_HOURS_CACHE_DURATION) {
        return parsedCache.ranges
      }
    }
  }

  if (pendingFetch !== null) {
    return pendingFetch
  }

  pendingFetch = (async () => {
    try {
      const response = await railsPostFetch('/api/v1/skipper/chats/working_hours', {
        method: 'GET'
      })

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch working hours:', response.status)
        return null
      }

      const ranges = (await response.json()) as WorkingHoursRange[]

      if (typeof window !== 'undefined' && window.sessionStorage) {
        const cacheData: CachedWorkingHours = {
          ranges,
          timestamp: Date.now()
        }
        window.sessionStorage.setItem(WORKING_HOURS_CACHE_KEY, JSON.stringify(cacheData))
      }

      return ranges
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching working hours:', error)
      return null
    } finally {
      pendingFetch = null
    }
  })()

  return pendingFetch
}

export function getMinutesSinceSundayMidnight(date: Date = new Date()): number {
  const utcDay = date.getUTCDay()
  const utcHours = date.getUTCHours()
  const utcMinutes = date.getUTCMinutes()

  return utcDay * 24 * 60 + utcHours * 60 + utcMinutes
}

export function isWithinWorkingHours(ranges: WorkingHoursRange[], currentTime: Date = new Date()): boolean {
  if (!ranges || ranges.length === 0) {
    return false
  }

  const minutesSinceSunday = getMinutesSinceSundayMidnight(currentTime)

  return ranges.some(range => minutesSinceSunday >= range.start_time && minutesSinceSunday < range.end_time)
}

export function clearWorkingHoursCache(): void {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.removeItem(WORKING_HOURS_CACHE_KEY)
  }
}

export async function isSupportAvailable(): Promise<boolean> {
  const ranges = await fetchWorkingHours()
  if (ranges === null) {
    return false
  }
  return isWithinWorkingHours(ranges)
}
